import {useCallback, useEffect, useLayoutEffect, useMemo} from 'react'
import type {PreloadedQuery, PreloadedEntryPoint} from 'react-relay/relay-hooks/EntryPointTypes'

import type {FetchBuildDataParams} from '../../../../../actions/builds'
import BuildLineLayout from '../../../../../containers/BuildLineLayout'
import BuildsFetcher, {getEvents} from '../../../../../containers/BuildsFetcher'
import BuildsWithLastReadyLocator from '../../../../../containers/BuildsWithLastReadyLocator'
import {BuildSelectionIdProvider} from '../../../../../contexts/buildSelection'
import {LocatorContext} from '../../../../../contexts/locator'
import {UpdateProvider} from '../../../../../contexts/update'
import {useAppSelector} from '../../../../../hooks/react-redux'
import {PlaceIdList} from '../../../../../plugins/plugins.types'
import {getBuildType} from '../../../../../selectors'
import type {Branch} from '../../../../../services/rest'
import type {BuildTypeId} from '../../../../../types'
import {stringifyBranch} from '../../../../../utils/branchNames'
import {noop} from '../../../../../utils/empty'
import type {Refetchable} from '../../../../../utils/refetchable'
import type {SimpleStream} from '../../../../../utils/simpleStream'
import {subscribeOnBuildTypeEvents} from '../../../../../utils/subscriber'
import BuildBatchActions from '../../../../common/Builds/BuildBatchActions/BuildBatchActions'
import BuildsFilters from '../../../../common/BuildsFilters/BuildsFilters.container'
import BuildPager from '../../../../common/Pager/BuildPager/BuildPager.container'
import {usePage} from '../../../../common/Pager/Pager.hooks'
import {PagerGroup} from '../../../../common/Pager/Pager.types'
import type {Pager} from '../../../../common/Pager/Pager.types'
import type {PermalinksQuery} from '../../../../common/Permalinks/__generated__/PermalinksQuery.graphql'
import Permalinks from '../../../../common/Permalinks/Permalinks'
import Plugin from '../../../../common/Plugin/Plugin'
import SequenceLoader from '../../../../common/SequenceLoader/SequenceLoader'
import type {TagsFiltersContainerComponent} from '../../../../common/TagsList/TagsFilters'

import styles from './BuildTypeHistory.css'

import {useRefetch} from 'src/hooks/useRefetch'

type Props = {
  pageSize?: number
  submitPager?: (pager: Partial<Pager>, group: PagerGroup) => unknown
  shouldLoadRunningAndQueued?: boolean
  showQueuedBuilds?: boolean
  hasQueuedOnly?: boolean
  hasBuilds?: boolean
  isSakuraUI?: boolean
  fetchBuildsData?: (params: FetchBuildDataParams) => Refetchable
  withFilters?: boolean
  branch?: Branch
  buildCount?: number | null | undefined
  buildTypeId: BuildTypeId
  showAgent?: boolean
  updatePeriod?: number | null | undefined
  update$?: SimpleStream<void>
  hasBranches?: boolean
  withRunningAndQueued?: boolean | null | undefined
  withCollapsedQueued?: boolean
  locator?: string
  composite?: boolean
  permalinksQuery?: PreloadedQuery<PermalinksQuery>
  tagsFiltersEntryPoint?: PreloadedEntryPoint<TagsFiltersContainerComponent>
}

const DEFAULT_PAGE_SIZE = 50
function BuildTypeHistory({
  submitPager = noop,
  pageSize = DEFAULT_PAGE_SIZE,
  shouldLoadRunningAndQueued = false,
  showQueuedBuilds = false,
  hasQueuedOnly = false,
  hasBuilds = false,
  isSakuraUI = false,
  fetchBuildsData,
  withFilters = true,
  buildTypeId,
  branch,
  showAgent,
  updatePeriod,
  update$,
  hasBranches,
  withCollapsedQueued,
  withRunningAndQueued,
  locator,
  composite,
  permalinksQuery,
  tagsFiltersEntryPoint,
}: Props) {
  useLayoutEffect(() => {
    submitPager(
      {
        pageSize,
      },
      PagerGroup.BUILD,
    )
  }, [pageSize, submitPager])
  const page = usePage()

  const fetch = useCallback(
    (args: {inBackground?: boolean; forceRefetch?: boolean}) => {
      const {inBackground = false, forceRefetch = false} = args
      return locator != null && fetchBuildsData != null
        ? fetchBuildsData({
            locator,
            withPager: true,
            page,
            inBackground,
            forceRefetch,
            requestOptions: {
              withBuildTypeDetails: true,
              withSnapshotDependencies: false,
              withQueuedInfo: true,
              withRunningInfo: true,
            },
          })
        : null
    },
    [fetchBuildsData, locator, page],
  )
  const refetch = useRefetch(fetch)
  const internalId = useAppSelector(state => getBuildType(state, buildTypeId)?.internalId)
  useEffect(
    () =>
      internalId != null
        ? subscribeOnBuildTypeEvents(
            internalId,
            getEvents(true, true),
            refetch,
            // eslint-disable-next-line @typescript-eslint/no-magic-numbers
            2000,
          )
        : undefined,
    [internalId, refetch],
  )

  const toolbar = useMemo(
    () => (
      <div className={styles.toolbarContainer}>
        {hasBuilds ? (
          withFilters && (
            <BuildsFilters
              disableAdvancedMode
              withStateFilter
              withTagFilter
              buildTypeId={buildTypeId}
              withAgentPatternFilter={!composite}
              withPager
              withRunningAndQueued={withRunningAndQueued}
              className={styles.filterContainer}
              tagsFiltersEntryPoint={tagsFiltersEntryPoint}
            />
          )
        ) : (
          <span className={styles.noBuilds}>{`No ${
            withRunningAndQueued === true ? 'running or ' : ''
          } finished builds`}</span>
        )}
      </div>
    ),
    [buildTypeId, composite, hasBuilds, tagsFiltersEntryPoint, withFilters, withRunningAndQueued],
  )

  const content = useMemo(
    () => (
      <>
        <Plugin placeId={PlaceIdList.SAKURA_BUILD_CONFIGURATION_BUILDS} />
        {toolbar}
        <SequenceLoader showFirstLoadingItem loader="">
          <BuildLineLayout
            seqKey="builds"
            showAgent={showAgent}
            showPath={false}
            showBranch={hasBranches}
          >
            <BuildsWithLastReadyLocator
              alwaysShowLineBorders={!isSakuraUI}
              showOverlayOnLoading
              withCollapsedQueued={withCollapsedQueued}
              showQueuedBuilds={showQueuedBuilds}
              hasQueuedOnly={hasQueuedOnly}
              withPager
              withLoader
              withTitle
              wrapped={isSakuraUI}
            />
          </BuildLineLayout>
          <BuildPager seqKey="pager" />
          <Permalinks
            seqKey="permalinks"
            buildTypeId={buildTypeId}
            branch={stringifyBranch(branch)}
            queryRef={permalinksQuery}
          />
        </SequenceLoader>
      </>
    ),
    [
      branch,
      buildTypeId,
      hasBranches,
      hasQueuedOnly,
      isSakuraUI,
      permalinksQuery,
      showAgent,
      showQueuedBuilds,
      toolbar,
      withCollapsedQueued,
    ],
  )

  return hasBuilds && locator != null ? (
    isSakuraUI ? (
      <UpdateProvider update={refetch}>
        <LocatorContext.Provider value={locator}>
          {' '}
          <BuildSelectionIdProvider>
            {content}
            <BuildBatchActions buildTypeId={buildTypeId} message />
          </BuildSelectionIdProvider>
        </LocatorContext.Provider>
      </UpdateProvider>
    ) : (
      <BuildsFetcher
        update$={update$}
        updatePeriod={updatePeriod}
        withPager
        withRunning={shouldLoadRunningAndQueued}
        withQueued={shouldLoadRunningAndQueued}
        locator={locator}
        withRunningAndQueued={shouldLoadRunningAndQueued}
        buildTypeId={buildTypeId}
      >
        {content}
      </BuildsFetcher>
    )
  ) : (
    toolbar
  )
}

export default BuildTypeHistory
